/**
 * ToastContext — lightweight toast notification system.
 * Renders toasts above the bottom nav (z-index 200) with a 4-second auto-dismiss.
 *
 * Usage:
 *   const { show } = useToast();
 *   show("Your message here.");
 */
import { createContext, useContext, useState, useCallback } from 'react';
import { X } from 'lucide-react';

const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const show = useCallback((message, { type = 'success' } = {}) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, message, type }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 4000);
  }, []);

  const dismiss = useCallback((id) => {
    setToasts((t) => t.filter((x) => x.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ show }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx;
}

// ── Toast container ────────────────────────────────────────────────────────────

function ToastContainer({ toasts, onDismiss }) {
  if (toasts.length === 0) return null;
  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      style={{
        position:       'fixed',
        /* Nav is now top-anchored — toasts sit near the bottom edge + safe-area. */
        bottom:         'calc(env(safe-area-inset-bottom) + 20px)',
        left:           '50%',
        transform:      'translateX(-50%)',
        zIndex:         200,
        display:        'flex',
        flexDirection:  'column',
        gap:            '8px',
        alignItems:     'center',
        pointerEvents:  'none',
        width:          'calc(100% - 32px)',
        maxWidth:       '380px',
      }}
    >
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

function Toast({ toast, onDismiss }) {
  return (
    <div
      role="status"
      style={{
        display:       'flex',
        alignItems:    'center',
        justifyContent:'space-between',
        gap:           '12px',
        padding:       '12px 14px 12px 16px',
        background:    'var(--c-surface-2)',
        border:        '1px solid rgba(232,160,32,0.28)',
        borderRadius:  '12px',
        boxShadow:     '0 4px 24px var(--c-shadow)',
        width:         '100%',
        boxSizing:     'border-box',
        pointerEvents: 'all',
        animation:     'toast-in 220ms ease',
      }}
    >
      {/* Amber accent stripe */}
      <div
        aria-hidden="true"
        style={{
          width:        '3px',
          height:       '32px',
          borderRadius: '999px',
          background:   'var(--c-gold)',
          flexShrink:   0,
        }}
      />
      <span
        style={{
          fontFamily: 'var(--font-body)',
          fontSize:   '14px',
          color:      'var(--c-text)',
          lineHeight: 1.4,
          flex:       1,
        }}
      >
        {toast.message}
      </span>
      <button
        type="button"
        aria-label="Dismiss notification"
        onClick={() => onDismiss(toast.id)}
        style={{
          flexShrink:     0,
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'center',
          width:          '28px',
          height:         '28px',
          background:     'transparent',
          border:         'none',
          cursor:         'pointer',
          color:          'var(--c-muted)',
          borderRadius:   '6px',
          padding:        0,
          transition:     'color 150ms ease',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--c-text)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--c-muted)'; }}
      >
        <X size={14} aria-hidden="true" />
      </button>
    </div>
  );
}
